import { useMemo, useCallback } from 'react';
import { BoltIcon, DocumentTextIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';
import { useAtom, useAtomValue } from 'jotai';

import { cn, FileType, sliceContent } from '@restworld/utils-common';
import { UploadCard } from '../../../legacy';
import { Heading, Button } from '@restworld/ui-ds-base';

import { useEntryPointCheckListContext } from './entry-component.context';
import { TitleWithBackBtn } from '.';
import {
  doesSizeExceedsAtom,
  spontaneousApplyDataAtom,
  uploadFilesAtom,
  workerEntryPointErrorsAtom,
} from '../../worker-entry-point.atoms';
import { CV_REFUSALS } from '../../worker-entry-point.constant';

export const UploadCVUI = ({ hideBackBtn }: { hideBackBtn?: boolean }) => {
  const [uploadFiles, setUploadFiles] = useAtom(uploadFilesAtom);
  const [, setSizeExceeds] = useAtom(doesSizeExceedsAtom);
  const spontaneousApplyData = useAtomValue(spontaneousApplyDataAtom);
  const isSpontaneousApplication = spontaneousApplyData?.isSpontaneousEntry;

  const { isCVNotUploaded, setIsCVNotUploaded } = useEntryPointCheckListContext();
  const { t } = useTranslation('onboarding-page');

  const handleUploadFiles = useCallback(
    (value: FileType) => {
      setUploadFiles(value);
      if (value?.[0] && value?.[0].size / 1000000 > 20) {
        setSizeExceeds(true);
      }
      setIsCVNotUploaded(false);
    },
    [setIsCVNotUploaded, setSizeExceeds, setUploadFiles]
  );

  return (
    <UploadEntryWrapper>
      <Wrapper>
        <TitleWithBackBtn
          title={t(
            isSpontaneousApplication ? 'upload_your_cv_to_send_a_spontaneous_application' : 'upload_your_cv_to_apply'
          )}
          testId="upload-your-cv-title"
          hideBackBtn={hideBackBtn}
        />
        <span className="flex flex-row items-center gap-1.5 justify-center">
          <BoltIcon className="h-4" />
          <Heading size={6} color="muted" weight="light">
            {t('it_will_take_less_than45_seconds')}
          </Heading>
        </span>
      </Wrapper>
      {!uploadFiles?.[0] ? (
        <UploadCard
          isError={isCVNotUploaded}
          setUploadFiles={handleUploadFiles}
          fileType={['.pdf', '.doc', '.docx', '.jpg']}
          testId="entry-point-upload-card"
        />
      ) : (
        <UploadedCVUI testId="entry-point-uploaded-cv-view" />
      )}
    </UploadEntryWrapper>
  );
};
const UploadEntryWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-6', className)} {...props} />
);
const Wrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-4', className)} {...props} />
);
const ErrorInfoText = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('p-2 text-xs text-red-700', className)} {...props} />
);

export const UploadedCVUI = ({ testId }: { testId?: string }) => {
  const [uploadFiles, setUploadFiles] = useAtom(uploadFilesAtom);
  const [doesSizeExceeds, setSizeExceeds] = useAtom(doesSizeExceedsAtom);
  const [errors, setErrors] = useAtom(workerEntryPointErrorsAtom);

  const handleDelete = useCallback(() => {
    setUploadFiles([]);
    setErrors({});
    setSizeExceeds(false);
  }, [setErrors, setSizeExceeds, setUploadFiles]);

  const couldNotParse = useMemo(() => errors && !!Object.keys(errors).length, [errors]);
  // Checked before the generic message: these bodies are not empty either, and "we could not
  // read your CV, upload another one" sends the person back with the same joint CV, or the same
  // locked one, having told them nothing about what to change.
  const refusal = errors?.error_code ? CV_REFUSALS[errors.error_code] : undefined;

  const { t } = useTranslation('onboarding-page');
  return (
    <>
      <div data-cy={testId} className="bg-primary-50 flex gap-4 items-center justify-between rounded-lg w-full p-3">
        <div className="flex gap-4 items-center">
          <div className="rounded-lg border bg-white p-3">
            <DocumentTextIcon className="h-6 w-6 text-primary-800" />
          </div>
          {uploadFiles?.[0] ? (
            <div className="flex flex-col gap-2">
              <Heading size={6} testId="upload-file-name-text">
                {uploadFiles?.[0]?.name && sliceContent(uploadFiles?.[0]?.name, 20)}
              </Heading>
              <Heading size={6} weight="medium" color="muted" testId="upload-file-size-text">{`${(
                uploadFiles?.[0]?.size / 1000000
              ).toFixed(2)}MB`}</Heading>
            </div>
          ) : null}
        </div>
        <Button StartIcon={TrashIcon} variant="tertiary" onClick={handleDelete} testId="upload-file-delete-button" />
      </div>
      {doesSizeExceeds && (
        <ErrorInfoText data-cy="upload-cv-size-exceed-warning">
          {t('the_cv_exceeds20mb_compress_it_or_choose_another_one')}{' '}
        </ErrorInfoText>
      )}
      {refusal ? (
        <ErrorInfoText data-cy={refusal.testId}>{t(refusal.messageKey)}</ErrorInfoText>
      ) : (
        <UploadErrorText statusCode={errors?.statusCode} couldNotParse={couldNotParse} />
      )}
    </>
  );
};

const UploadErrorText = ({ statusCode, couldNotParse }: { statusCode?: number; couldNotParse: boolean }) => {
  const { t } = useTranslation('onboarding-page');

  if (statusCode === 415) {
    return <ErrorInfoText data-cy="upload-cv-status-code-415-warning">{t('common:upload_pdf_cv')} </ErrorInfoText>;
  }

  if (!couldNotParse) return null;

  return (
    <ErrorInfoText data-cy="upload-cv-could-not-parse-error">
      {t('we_were_unable_to_read_your_cv,we_suggest_you_upload_another_one')}{' '}
      <span role="img" aria-labelledby="wink">
        😉
      </span>
    </ErrorInfoText>
  );
};
